import { useEffect, useState } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import { useDealer } from '../../context/DealerContext';
import { dealerApi, DealerUser } from '../../services/dealerApi';
import StatusChip from '../../components/dealer/StatusChip';
import { dealerErrorMessage } from '../../utils/dealer';

/**
 * Spec 085 (BR-6) — Settings → Users: the owner grants or removes a staff user's canCommitSpend
 * (accept proformas, buy packs). Staff see their own permission only; users are added by Garagat.
 */
export default function DealerSettingsPage() {
  const { t } = useLanguage();
  const { token, me, isOwner, canCommitSpend } = useDealer();
  const [users, setUsers] = useState<DealerUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !isOwner) return;
    dealerApi.users(token).then(setUsers).catch((err) => setError(dealerErrorMessage(err, t)));
  }, [token, isOwner, t]);

  const toggle = async (u: DealerUser) => {
    if (!token) return;
    setSavingId(u.id);
    setError(null);
    setNotice(null);
    try {
      const updated = await dealerApi.setCommitSpend(u.id, !u.canCommitSpend, token);
      setUsers((prev) => (prev ?? []).map((x) => (x.id === updated.id ? updated : x)));
      setNotice(t('dealer.settings.saved'));
    } catch (err) {
      setError(dealerErrorMessage(err, t));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="dealer-page">
      <div className="dealer-page__header">
        <div>
          <h2>{t('dealer.settings.title')}</h2>
          <p className="dealer-muted">{me?.dealer.legalName}</p>
        </div>
      </div>
      <div className="dealer-card">
        <h3>{t('dealer.settings.myAccess')}</h3>
        <p>{t(isOwner ? 'dealer.settings.roleOwner' : 'dealer.settings.roleStaff')}</p>
        <p className="dealer-muted">{t(canCommitSpend ? 'dealer.settings.canCommit' : 'dealer.settings.cannotCommit')}</p>
      </div>
      {!isOwner ? (
        <p className="dealer-note">{t('dealer.settings.ownerOnly')}</p>
      ) : (
        <div className="dealer-card">
          <h3>{t('dealer.settings.users')}</h3>
          <p className="dealer-muted">{t('dealer.settings.usersHint')}</p>
          {notice && <div className="success-message" role="status">{notice}</div>}
          {error && <div className="error-message">{error}</div>}
          {!users && !error && <p className="dealer-muted">{t('dealer.common.loading')}</p>}
          {users && (
            <div className="dealer-table-wrap">
              <table className="dealer-table">
                <thead>
                  <tr>
                    <th>{t('dealer.settings.name')}</th>
                    <th>{t('dealer.settings.contact')}</th>
                    <th>{t('dealer.settings.role')}</th>
                    <th>{t('dealer.credits.status')}</th>
                    <th>{t('dealer.settings.commitSpend')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>{u.name}</td>
                      <td><div dir="ltr">{u.email}</div><div className="dealer-muted" dir="ltr">{u.phone}</div></td>
                      <td>{t(u.role === 'OWNER' ? 'dealer.settings.owner' : 'dealer.settings.staff')}</td>
                      <td><StatusChip status={u.status} /></td>
                      <td>
                        {u.role === 'OWNER' ? (
                          <span className="dealer-muted">{t('dealer.settings.always')}</span>
                        ) : (
                          <label className="dealer-switch">
                            <input
                              type="checkbox"
                              role="switch"
                              aria-label={`${t('dealer.settings.commitSpend')} — ${u.name}`}
                              checked={u.canCommitSpend}
                              disabled={savingId === u.id || u.status !== 'ACTIVE'}
                              onChange={() => toggle(u)}
                            />
                            <span>{t(u.canCommitSpend ? 'dealer.settings.allowed' : 'dealer.settings.notAllowed')}</span>
                          </label>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
